import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import ContentCopy from '@mui/icons-material/ContentCopy';
import MailOutlined from '@mui/icons-material/MailOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import Modal from "../../components/Modal/Modal";
import axiosInstance from "../../utils/API";
const extractFilename = (contentDisposition) => {
  if (!contentDisposition) return null;
  const match =
    contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) ||
    contentDisposition.match(/filename="?([^"]+)"?/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).replace(/(^"|"$)/g, "");
  } catch {
    return match[1].replace(/(^"|"$)/g, "");
  }
};

const isLikelyImageName = (name = "") => {
  const n = name.toLowerCase().trim();
  return (
    n.endsWith(".png") ||
    n.endsWith(".jpg") ||
    n.endsWith(".jpeg") ||
    n.endsWith(".gif") ||
    n.endsWith(".webp") ||
    n.endsWith(".bmp") ||
    n.endsWith(".svg")
  );
};

const extractDownloadLinks = (html = "") => {
  const links = [];
  const re = /<a[^>]+href="(#download:([^"]+))"[^>]*>(.*?)<\/a>/gi;

  let m;
  while ((m = re.exec(html)) !== null) {
    const fullHref = m[1];
    const id = m[2];
    const text = (m[3] || "").replace(/<[^>]*>/g, "").trim();
    links.push({ id, href: fullHref, text });
  }

  const seen = new Set();
  return links.filter((x) => {
    if (!x.id) return false;
    if (seen.has(x.id)) return false;
    seen.add(x.id);
    return true;
  });
};

const removeAllImages = (html = "") => {
  return html.replace(/<img[^>]*>/gi, "");
};

const MessageViewerWithAttachments = ({ html }) => {
  const [imagePreviews, setImagePreviews] = useState({});
  const blobUrlsRef = useRef([]);

  const cleanedHtml = useMemo(
  () => removeAllImages(html || ""),
  [html]
);
  const attachments = useMemo(() => extractDownloadLinks(cleanedHtml), [cleanedHtml]);

  const secureOpenById = async (id, filenameFallback) => {
    const res = await axiosInstance.get(`/candidatsmanagement/downloadfile/${id}`, {
      responseType: "blob",
    });

    const contentType = res.headers?.["content-type"] || "";
    const dispo = res.headers?.["content-disposition"] || "";
    const filename = extractFilename(dispo) || filenameFallback || "download";

    const blob = new Blob([res.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    blobUrlsRef.current.push(blobUrl);

    const canPreview = contentType.startsWith("image/") || contentType === "application/pdf";

    if (canPreview) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const imgAtt = attachments.filter((a) => isLikelyImageName(a.text));

      for (const a of imgAtt) {
        if (imagePreviews[a.id]) continue;

        try {
          const res = await axiosInstance.get(
            `/candidatsmanagement/downloadfile/${a.id}?inline=1`,
            { responseType: "blob" }
          );

          if (cancelled) return;

          const url = URL.createObjectURL(res.data);
          blobUrlsRef.current.push(url);

          setImagePreviews((prev) => ({ ...prev, [a.id]: url }));
        } catch {
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [attachments]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
      blobUrlsRef.current = [];
    };
  }, []);
  const onClickCapture = async (e) => {
    const a = e.target?.closest?.("a");
    if (!a) return;

    const href = a.getAttribute("href") || "";
    if (!href.startsWith("#download:")) return;

    e.preventDefault();
    e.stopPropagation();

    const id = href.replace("#download:", "");
    const filenameFallback = (a.textContent || "").trim();

    await secureOpenById(id, filenameFallback);
  };

  const images = attachments.filter((a) => isLikelyImageName(a.text));
  const docs = attachments.filter((a) => !isLikelyImageName(a.text));

  return (
    <div onClickCapture={onClickCapture}>
      <div
        style={{ whiteSpace: "normal" }}
        dangerouslySetInnerHTML={{ __html: cleanedHtml }}
      />

      {(images.length > 0 || docs.length > 0) && (
        <div style={{ marginTop: 12 }}>
          {images.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Images jointes</div>
              {images.map((img) => (
                <div key={img.id} style={{ marginBottom: 10 }}>
                  {imagePreviews[img.id] ? (
                    <img
                      src={imagePreviews[img.id]}
                      alt={img.text}
                      style={{
                        maxWidth: "100%",
                        maxHeight: 260,
                        display: "block",
                        borderRadius: 6,
                        border: "1px solid #eee",
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.7 }}>Chargement image…</div>
                  )}

                  <a href={`#download:${img.id}`} style={{ display: "inline-block", marginTop: 6 }}>
                    {img.text || `Image (${img.id})`}
                  </a>
                </div>
              ))}
            </div>
          )}
          {docs.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Pièces jointes</div>
              {docs.map((d) => (
                <div key={d.id} style={{ marginBottom: 6 }}>
                  <a href={`#download:${d.id}`}>{d.text || `Document (${d.id})`}</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Template variables such as [Prenom_CDT] are replaced when the message is sent:
// show them as highlighted tokens in the preview.
const highlightVariables = (html = "") =>
  html.replace(/\[([A-Za-z0-9_]+)\]/g, '<span class="tpl-var">[$1]</span>');

const htmlToText = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").replace(/ /g, " ").trim();
};

// GSM-7 SMS: 160 characters for one SMS, 153 per part beyond that.
const smsParts = (len) => (len <= 160 ? 1 : Math.ceil(len / 153));

const TYPE_STYLES = {
  sms: { label: "SMS", Icon: SmsOutlined, color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  email: { label: "Email", Icon: MailOutlined, color: "#03407b", bg: "#ebf4fb", border: "#aed6f1" },
};

const previewSx = {
  fontSize: "0.9rem", color: "#1f2937", lineHeight: 1.6, wordBreak: "break-word",
  "& p": { m: 0, mb: 1 },
  "& a": { color: "#0369a1" },
  "& .tpl-var": {
    display: "inline-block", px: 0.5, mx: 0.25, borderRadius: "4px",
    background: "#fef3c7", color: "#92400e", fontWeight: 600, fontSize: "0.82em", fontFamily: "monospace",
  },
};

const EmailView = ({ activite, msgName, objet, message, TypeMsg }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const isSms = String(TypeMsg || "").toLowerCase().includes("sms");
  const type = isSms ? TYPE_STYLES.sms : TYPE_STYLES.email;
  const safeHtml = useMemo(() => highlightVariables(DOMPurify.sanitize(message || "")), [message]);
  const plainText = useMemo(() => htmlToText(DOMPurify.sanitize(message || "")), [message]);
  const variables = useMemo(() => [...new Set((plainText.match(/\[[A-Za-z0-9_]+\]/g) || []))], [plainText]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const body = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Chip icon={<type.Icon sx={{ fontSize: "16px !important", color: `${type.color} !important` }} />} label={type.label} size="small"
          sx={{ height: 24, fontWeight: 700, color: type.color, background: type.bg, border: `1px solid ${type.border}` }} />
        {activite && (
          <Chip label={`Activité : ${activite}`} size="small" sx={{ height: 24, fontWeight: 600, color: "#475569", background: "#f1f5f9", textTransform: "capitalize" }} />
        )}
        <Tooltip title={copied ? "Copié !" : "Copier le texte du message"}>
          <IconButton size="small" onClick={handleCopy} sx={{ ml: "auto", color: "#64748b", "&:hover": { color: "#03407b" } }}>
            <ContentCopy sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase" }}>Intitulé</Typography>
        <Typography sx={{ fontSize: "1.02rem", fontWeight: 700, color: "#0f172a" }}>{msgName || "—"}</Typography>
      </Box>

      {isSms ? (
        <Box sx={{ p: 2, borderRadius: "14px", background: "#f1f5f9", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75 }}>
          <Box sx={{ maxWidth: "85%", px: 1.75, py: 1.25, borderRadius: "16px 16px 16px 4px", background: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,0.08)" }}>
            <Box sx={previewSx}><MessageViewerWithAttachments html={safeHtml} /></Box>
          </Box>
          <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
            {plainText.length} caractères · {smsParts(plainText.length)} SMS
            {variables.length > 0 && " (avant remplacement des variables)"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.25, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>Objet</Typography>
            <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: objet ? "#0f172a" : "#cbd5e1", fontStyle: objet ? "normal" : "italic" }}>
              {objet || "Aucun objet"}
            </Typography>
          </Box>
          <Box sx={{ ...previewSx, px: 2, py: 1.75, maxHeight: "48vh", overflowY: "auto" }}>
            <MessageViewerWithAttachments html={safeHtml} />
          </Box>
        </Box>
      )}

      {variables.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>Variables remplacées à l'envoi :</Typography>
          {variables.map((v) => (
            <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: "0.68rem", fontFamily: "monospace", fontWeight: 600, color: "#92400e", background: "#fef3c7" }} />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Tooltip title="Aperçu du message">
        <IconButton size="small" onClick={() => setOpen(true)} sx={{ color: "#03407b" }}>
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Aperçu du message ${isSms ? "SMS" : "email"}`}
        maxWidth="sm"
      >
        {body}
      </Modal>
    </>
  );
};

export default EmailView;
